import type { Type } from '@angular/core';

import { COMPONENT_REGISTRY } from '../../../core/registry/component-registry';

/**
 * Slug used by the unified dashboard grid to pick which embedded report
 * component to render inside `app-widget-host`. Keys here MUST match the
 * `reportKey` produced by `resolveReportKey()` in `my-dashboard.component.ts`
 * (and the cases of the legacy `@switch (tile.reportKey)` block we replaced).
 */
export type EmbeddedReportKey =
  | 'monthly-report-1'
  | 'monthly-report-2'
  | 'social-platform'
  | 'seo-stats'
  | 'seo-overview'
  | 'campaign-ads-stats'
  | 'industry-categorization'
  | 'training-dashboard'
  | 'business-performance-report'
  | 'contracts-report'
  | 'practina-dashboard'
  | 'lendee-dashboard'
  | 'lendee-analytics-dashboard'
  | 'cool-credit-dashboard'
  | 'growth-activation-and-revenue'
  | 'daily-cron-payments'
  | 'overdue-cron-payments'
  | 'arrangement-cron-overview'
  | 'arrangement-cron-trends'
  | 'arrangement-cron-admin-performance';

/**
 * Maps each `EmbeddedReportKey` to the class name registered in the
 * app-wide `COMPONENT_REGISTRY` (see
 * `src/app/core/registry/component-registry.ts`). We deliberately re-use
 * that existing eager registry instead of declaring our own dynamic
 * `import()` loaders — `AuthService` already pulls every report
 * component into the main chunk at boot via `COMPONENT_REGISTRY`, so a
 * second (lazy) chunk path here only confuses esbuild's chunking and can
 * cause `Class extends value undefined` errors when the report's
 * base-class chunk evaluates AFTER the report itself.
 */
const REPORT_KEY_TO_REGISTRY_NAME: Record<EmbeddedReportKey, string> = {
  'monthly-report-1':                  'MonthlyReport1Component',
  'monthly-report-2':                  'MonthlyReport2Component',
  'social-platform':                   'SocialPlatformComponent',
  'seo-stats':                         'SeoStatsComponent',
  'seo-overview':                      'SeoOverviewComponent',
  'campaign-ads-stats':                'CampaignAdsStatsComponent',
  'industry-categorization':           'IndustryWiseCategorizationComponent',
  'training-dashboard':                'TrainingDashboardComponent',
  'business-performance-report':       'BusinessPerformanceReportComponent',
  'contracts-report':                  'ContractsReportComponent',
  'practina-dashboard':                'PractinaDashboardComponent',
  'lendee-dashboard':                  'LendeeDashboardComponent',
  'lendee-analytics-dashboard':        'LendeeAnalyticsDashboardComponent',
  'cool-credit-dashboard':             'CoolCreditDashboardComponent',
  'growth-activation-and-revenue':     'GrowthActivationAndRevenueComponent',
  'daily-cron-payments':               'DailyCronComponent',
  'overdue-cron-payments':             'OverdueCronComponent',
  'arrangement-cron-overview':         'OverviewNewComponent',
  'arrangement-cron-trends':           'TrendsComponent',
  'arrangement-cron-admin-performance':'AdminPerformanceComponent',
};

/**
 * Loader map consumed by `WidgetHostComponent`. Each entry returns a
 * Promise that resolves to the report component class so
 * `ViewContainerRef.createComponent()` can mount it.
 *
 * Implementation note: the loaders are synchronous registry lookups
 * wrapped in `Promise.resolve(...)`. The async signature is preserved
 * so we can swap to true `import()` lazy loading later (per-report)
 * once these components are detached from the eager
 * `COMPONENT_REGISTRY` route table.
 *
 * Adding a new report widget = ONE new line in
 * `REPORT_KEY_TO_REGISTRY_NAME` (plus the matching string literal in
 * `EmbeddedReportKey`), assuming the component is already registered
 * in `COMPONENT_REGISTRY`.
 */
export const WIDGET_REGISTRY: Record<EmbeddedReportKey, () => Promise<Type<unknown>>> =
  Object.freeze(
    (Object.keys(REPORT_KEY_TO_REGISTRY_NAME) as EmbeddedReportKey[]).reduce(
      (acc, key) => {
        const registryName = REPORT_KEY_TO_REGISTRY_NAME[key];
        acc[key] = () => {
          const cmp = COMPONENT_REGISTRY[registryName] as Type<unknown> | undefined;
          if (!cmp) {
            return Promise.reject(
              new Error(
                `[widget-registry] '${registryName}' not found in COMPONENT_REGISTRY for reportKey='${key}'.`,
              ),
            );
          }
          return Promise.resolve(cmp);
        };
        return acc;
      },
      {} as Record<EmbeddedReportKey, () => Promise<Type<unknown>>>,
    ),
  );
